import { ROLES, normalizeRoleName } from "../Constants/RoleConstants";

/** Discovery Hub status chip row — 5-column responsive grid. */
export const STATUS_CHIP_ROW =
  "grid grid-cols-1 gap-2.5 mb-4 min-[481px]:grid-cols-2 min-[721px]:grid-cols-3 min-[1101px]:grid-cols-5";

const STATUS_CHIP_TEXT_LABEL =
  "text-xs font-semibold text-black-500 uppercase tracking-wide mb-1";

const STATUS_CHIP_TEXT_VALUE =
  "text-2xl font-bold text-gray-900 leading-tight tabular-nums";

const STATUS_CHIP_TEXT_VALUE_UNIT = "text-sm font-medium text-gray-500 ml-1.5";

const POLL_STATUS_CHIP_BASE =
  "bg-white border border-gray-200 rounded-lg py-3 pr-4 pl-4 shadow-sm";

const EXEC_STATUS_CHIP_BASE = `${POLL_STATUS_CHIP_BASE} flex flex-col justify-center min-h-[88px]`;

type PollStatusVariant =
  | "notstarted"
  | "inprogress"
  | "ontime"
  | "late"
  | "overdue";

type ExecKpiVariant =
  | "sponsors"
  | "composite"
  | "champion"
  | "atrisk"
  | "completion";

const POLL_STATUS_ACCENT: Record<PollStatusVariant, string> = {
  notstarted: "border-l-4 border-l-red-600",
  inprogress: "border-l-4 border-l-amber-600",
  ontime: "border-l-4 border-l-green-600",
  late: "border-l-4 border-l-blue-600",
  overdue: "border-l-4 border-l-red-900",
};

const EXEC_KPI_ACCENT: Record<ExecKpiVariant, string> = {
  sponsors: "border-l-4 border-l-purple-600",
  composite: "border-l-4 border-l-blue-600",
  champion: "border-l-4 border-l-green-600",
  atrisk: "border-l-4 border-l-red-600",
  completion: "border-l-4 border-l-amber-600",
};

const TIER_BAR_FILL: Record<string, string> = {
  Champion: "bg-green-700",
  Active: "bg-blue-700",
  Developing: "bg-amber-700",
  "At-Risk": "bg-red-700",
};

export const statusChipLabelClass = STATUS_CHIP_TEXT_LABEL;
export const statusChipValueClass = STATUS_CHIP_TEXT_VALUE;
export const statusChipValueUnitClass = STATUS_CHIP_TEXT_VALUE_UNIT;

export function pollStatusChipClass(variant: PollStatusVariant): string {
  return `${POLL_STATUS_CHIP_BASE} ${POLL_STATUS_ACCENT[variant]}`;
}

const LEADERBOARD_TIER_ACCENT: Record<string, string> = {
  Champion: "border-l-4 border-l-green-600",
  Active: "border-l-4 border-l-blue-600",
  Developing: "border-l-4 border-l-amber-600",
  "At-Risk": "border-l-4 border-l-red-600",
};

/** Tier KPI row — same white card + left accent as Ongoing Polls status chips. */
export const LEADERBOARD_TIER_CHIP_ROW =
  "grid grid-cols-1 gap-2.5 mb-4 min-[481px]:grid-cols-2 min-[721px]:grid-cols-4";

export function leaderboardTierStatusChipClass(tier: string): string {
  return `${POLL_STATUS_CHIP_BASE} ${LEADERBOARD_TIER_ACCENT[tier] ?? "border-l-4 border-l-gray-400"}`;
}

export function execStatusChipClass(variant: ExecKpiVariant): string {
  return `${EXEC_STATUS_CHIP_BASE} ${EXEC_KPI_ACCENT[variant]}`;
}

export function statusChipFootClass(direction?: "up" | "down"): string {
  const base = "text-[12px] text-gray-500 mt-1 leading-snug";
  if (direction === "up") return `${base} text-green-700 font-semibold`;
  if (direction === "down") return `${base} text-red-700 font-semibold`;
  return base;
}

export function tierBarFillClass(tier: string, prior = false): string {
  const base =
    "absolute inset-y-0 left-0 flex items-center pl-2 text-[11px] font-bold text-white min-w-0";
  if (prior) return `${base} bg-gray-400`;
  return `${base} ${TIER_BAR_FILL[tier] ?? "bg-gray-400"}`;
}

export function tierMixSegmentClass(tier: string): string {
  return `inline-block h-3.5 ${TIER_BAR_FILL[tier] ?? "bg-gray-400"}`;
}

/** White section card — matches Exec Ops section cards (Command Center, Accounts). */
export const DH_SECTION_CARD = "bg-white border border-gray-200 rounded-lg";

export const S7_ROOT = "min-w-0";

export const S7_STATE_BAR_META = "ml-auto text-[11px] font-medium opacity-85";

export function sponsorDrilldownStateBarClass(
  pubState: string | null | undefined
): string {
  const base =
    "flex items-center gap-2 py-2 px-3.5 rounded-lg text-[12.5px] font-semibold border";
  if (pubState === "published") {
    return `${base} bg-green-50 border-green-200 text-green-700`;
  }
  if (pubState === "pending_approval") {
    return `${base} bg-amber-50 border-amber-200 text-amber-900`;
  }
  return `${base} bg-purple-50 border-purple-200 text-purple-900`;
}

export function gapBadgeClass(
  variant: "aligned" | "warn" | "risk" | "insufficient"
): string {
  const base =
    "inline-flex items-center gap-1 py-0.5 px-2 rounded-[10px] text-[10.5px] font-bold whitespace-nowrap";
  const variants: Record<typeof variant, string> = {
    aligned: "bg-green-100 text-green-700",
    warn: "bg-amber-100 text-amber-900",
    risk: "bg-red-100 text-red-900",
    insufficient: "bg-gray-100 text-gray-500",
  };
  return `${base} ${variants[variant]}`;
}

export const S7_MUTED = "text-[11px] text-gray-500";

export const S7_HEADER = `${DH_SECTION_CARD} flex items-start gap-3.5 p-4 px-[18px] mb-3.5`;

export const S7_AVATAR =
  "w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-purple-500 text-white flex items-center justify-center text-lg font-bold shrink-0";

export const S7_INFO = "flex-1 min-w-0";

export const S7_NAME = "text-lg font-bold text-gray-800 mb-0.5";

export const S7_META = "text-xs text-gray-500 mb-2";

export const S7_STATS = "flex gap-[18px] flex-wrap";

export const S7_STAT = "text-xs text-gray-500";

export const S7_STAT_VALUE =
  "text-[15px] text-gray-800 font-bold block tabular-nums leading-tight";

export const S7_GRID =
  "grid grid-cols-2 gap-4 items-stretch max-[900px]:grid-cols-1";

export const S7_QUADRANT = `${DH_SECTION_CARD} shadow-sm overflow-hidden`;

export const S7_QUADRANT_H =
  "py-3.5 px-4 text-[13.5px] font-bold text-gray-800 flex items-center justify-between gap-2 border-b border-gray-200";

export const S7_QUADRANT_PERIOD = "text-[11px] font-normal text-gray-400";

export const S7_QUADRANT_SUB = "text-[11.5px] text-gray-500 mb-2.5";

export const S7_QUADRANT_EMPTY =
  "text-center py-[30px] text-gray-500 text-[13px]";

export const S7_CHART_SVG = "block mx-auto max-w-full h-auto";

export const S7_QUAD_LEGEND = "grid grid-cols-2 gap-1.5 mt-2.5 text-sm";

export const S7_QUAD_LEGEND_ITEM =
  "flex items-center gap-1.5 text-gray-700 py-1 px-2 rounded-[5px] bg-gray-50 border border-gray-100";

export const S7_QUAD_LEGEND_DOT = "w-2 h-2 rounded-full shrink-0";

export const S7_ACCOUNT_CARD = `${DH_SECTION_CARD} shadow-sm overflow-hidden`;

export const S7_ACCOUNT_CARD_H =
  "py-3.5 px-4 text-[13.5px] font-bold text-gray-800 flex items-center justify-between gap-2 border-b border-gray-200";

export const S7_ACCOUNT_META = "text-[11px] font-normal text-gray-500";

export const S7_DT = "w-full table-fixed border-collapse text-[13px] bg-white";

export const S7_DT_TH =
  "bg-gray-50 py-2.5 px-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wide border-b border-gray-200";

export const S7_DT_TH_NUM = S7_DT_TH;

export const S7_DT_TD =
  "py-2.5 px-3 border-b border-gray-200 align-middle text-left last:border-b-0";

export const S7_DT_TD_NUM = `${S7_DT_TD} tabular-nums`;

export const S7_DT_TR = "hover:bg-gray-50";

export const S7_ACCT_SUB = "text-[10.5px] text-gray-500 mt-0.5";

export const S7_SPARK = "block";

/** Root wrapper for draft / pending / snapshot leaderboard panels. */
export const LB_PANEL = "min-w-0";

export const LB_PAGE_HEADER = "mb-4";
export const LB_PAGE_TITLE = "text-xl font-bold text-gray-900 m-0 mb-1";
export const LB_PAGE_SUB = "text-sm text-gray-500 m-0";

const LB_STATE_BAR_BASE =
  "flex items-center gap-2.5 flex-wrap py-3 px-4 rounded-xl mb-3.5 text-[13px] border";

const LB_STATE_BAR_VARIANT: Record<
  "draft" | "pending" | "published" | "none",
  string
> = {
  draft: "bg-gray-50 border-gray-200 text-gray-700",
  pending: "bg-amber-50 border-amber-200 text-amber-900",
  published: "bg-green-50 border-green-200 text-green-700",
  none: "bg-purple-50 border-purple-200 text-purple-900",
};

export function lbStateBarClass(state: string | null | undefined): string {
  let variant: keyof typeof LB_STATE_BAR_VARIANT = "none";
  if (state === "published") variant = "published";
  else if (state === "pending_approval") variant = "pending";
  else if (state === "draft") variant = "draft";
  return `${LB_STATE_BAR_BASE} ${LB_STATE_BAR_VARIANT[variant]}`;
}

export const LB_STATE_TITLE = "font-bold";
export const LB_STATE_META =
  "ml-auto text-[11.5px] font-medium opacity-[0.85] tabular-nums text-right flex-1 min-w-0";
export const LB_STATE_META_STACKED = `${LB_STATE_META} flex flex-col items-end gap-0.5`;
export const LB_STATE_META_LINE = "block";
export const LB_STATE_ACTIONS = "shrink-0 ml-2";

const LB_STATUS_BANNER_BASE =
  "flex items-center gap-3 flex-wrap py-3.5 px-[18px] rounded-[14px] mb-4 border";

export function lbStatusBannerClass(variant: "pending" | "published"): string {
  if (variant === "published") {
    return `${LB_STATUS_BANNER_BASE} bg-green-50 border-green-200 text-green-700`;
  }
  return `${LB_STATUS_BANNER_BASE} bg-amber-50 border-amber-200 text-amber-900`;
}

export const LB_STATUS_BANNER_BODY = "flex-1 min-w-[200px]";
export const LB_STATUS_BANNER_TITLE = "text-sm font-bold leading-snug";
export const LB_STATUS_BANNER_META = "text-[12.5px] mt-0.5 opacity-90";

const LB_ACTION_BAR_BASE =
  "flex items-center gap-2.5 flex-wrap py-[11px] px-4 rounded-xl mb-3.5 border border-purple-200 bg-purple-50";

export function lbActionBarClass(approvalPending = false): string {
  if (approvalPending) {
    return `${LB_ACTION_BAR_BASE} bg-amber-100 border-amber-300`;
  }
  return LB_ACTION_BAR_BASE;
}

export const LB_ACTION_BAR_MSG =
  "flex-1 min-w-[200px] text-[12.5px] text-gray-700 font-medium m-0";
export const LB_ACTION_BAR_MSG_PENDING =
  "flex-1 min-w-[200px] text-[12.5px] text-amber-900 font-medium m-0";

export const LB_FILTERS =
  "flex gap-2 items-center mb-4 flex-wrap bg-white py-2.5 px-3 rounded-lg border border-gray-200 [&_select]:py-1.5 [&_select]:px-2.5 [&_select]:rounded-lg [&_select]:border [&_select]:border-gray-300 [&_select]:bg-white [&_select]:text-sm [&_select]:text-gray-800 [&_select]:cursor-pointer [&_input]:py-1.5 [&_input]:px-2.5 [&_input]:rounded-lg [&_input]:border [&_input]:border-gray-300 [&_input]:bg-white [&_input]:text-sm [&_input]:text-gray-800 [&_input]:w-[180px]";

export const LB_FILTER_LBL =
  "text-xs font-semibold text-gray-500 uppercase tracking-wide";

export const LB_QUARTER_DISPLAY = "ml-auto flex items-center gap-1.5";
export const LB_QUARTER_VALUE =
  "text-xs font-semibold text-gray-800 py-1.5 px-2.5 rounded-lg bg-gray-100 border border-gray-200";

export const LB_EMPTY =
  "border border-dashed border-gray-300 rounded-lg py-10 px-6 text-center bg-white text-gray-500 text-sm leading-relaxed [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:m-0 [&_h3]:mb-2 [&_p]:m-0 [&_p]:max-w-[420px] [&_p]:mx-auto";

export const DH_SECTION_ACCORDION =
  "mb-4 bg-white border border-gray-200 rounded-lg overflow-hidden";
export const DH_SECTION_ACCORDION_HDR =
  "flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-white";
export const DH_SECTION_ACCORDION_HEADING =
  "flex items-center gap-2 flex-1 min-w-0";
export const DH_SECTION_ACCORDION_TITLE = "text-lg font-semibold text-gray-900";
export const DH_SECTION_ACCORDION_COUNT =
  "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#974ae4] text-white rounded-full text-xs font-medium";
export const DH_SECTION_ACCORDION_BODY =
  "p-4 border-t border-gray-200 bg-gray-50";

export const DH_POLL_CYCLE_CARD =
  "bg-white border border-gray-200 rounded-lg overflow-hidden";
export const DH_POLL_CYCLE_CARD_HEADER = "p-6";
export const DH_POLL_CYCLE_CARD_BODY = "border-t border-gray-200 bg-white p-4";
export const DH_POLL_CYCLE_CHECKBOX =
  "w-[18px] h-[18px] accent-purple-600 cursor-pointer shrink-0";

export const DH_POLL_ACCORDION_SUBTABS = "mb-4 border-b border-gray-200";

export const DH_CYCLE_HISTORY_CARD =
  "bg-white border border-gray-200 rounded-lg mt-1.5 overflow-hidden";
export const DH_CYCLE_HISTORY_EMPTY =
  "py-8 px-4 text-center text-[13px] text-gray-500";
export const DH_CYCLE_HISTORY_TABLE_WRAP = "overflow-x-auto";

export const POLL_HISTORY_TABLE =
  "w-full border-collapse text-sm bg-white [&_th]:bg-gray-50 [&_th]:px-4 [&_th]:py-3 [&_th]:text-xs [&_th]:font-medium [&_th]:text-gray-500 [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-left [&_th]:border-b [&_th]:border-gray-200 [&_th]:whitespace-nowrap [&_td]:px-4 [&_td]:py-3 [&_td]:border-b [&_td]:border-gray-200 [&_td]:text-gray-700 [&_td]:align-middle [&_tbody_tr:last-child_td]:border-b-0 [&_tbody_tr:hover_td]:bg-gray-50";
export const POLL_HISTORY_SUBNAME = "text-sm font-normal text-gray-500 mt-0.5";
export const POLL_HISTORY_ACTIONS = "whitespace-nowrap";
export const POLL_HISTORY_ACTION_BTNS = "flex flex-wrap gap-1.5";

export const DH_PUBLISHED_LEADERBOARD = "min-w-0";

export type DiscoveryHubHubTab =
  | "ongoing"
  | "late-window"
  | "completed"
  | "draft-leaderboard"
  | "approval-pending"
  | "executive-summary"
  | "cycles";

const DISCOVERY_HUB_STANDALONE_ROLES = new Set<string>([
  ROLES.EXECUTIVE_SPONSOR_LEAD,
]);

const SPONSOR_DRILLDOWN_ROLES = new Set<string>([
  ROLES.VENDOR_ADMIN,
  ROLES.IZBA_ADMIN,
  ROLES.C_LEVEL,
  ROLES.EXECUTIVE_SPONSOR_LEAD,
  ROLES.EXECUTIVE_SPONSOR,
]);

/** Mirrors backend `_require_executive_ops` for sponsor drilldown access. */
export const canAccessSponsorDrilldown = (
  userRole: string | { name?: string } | null | undefined,
  executiveOps = false
): boolean => {
  if (executiveOps) return true;
  const role = normalizeRoleName(userRole);
  return !!role && SPONSOR_DRILLDOWN_ROLES.has(role);
};

export const discoveryHubReturnPath = (
  userRole: string | { name?: string } | null | undefined,
  hub: DiscoveryHubHubTab = "ongoing"
): string => {
  const role = normalizeRoleName(userRole);
  if (role && DISCOVERY_HUB_STANDALONE_ROLES.has(role)) {
    return `/discovery-hub?hub=${hub}`;
  }
  return `/ops?tab=discovery-hub&hub=${hub}`;
};

export const sponsorDrilldownAudienceLabel = (
  userRole: string | { name?: string } | null | undefined
): string => {
  const role = normalizeRoleName(userRole);
  if (role === ROLES.VENDOR_ADMIN) return "Vendor admin view";
  if (role === ROLES.C_LEVEL) return "Executive view";
  if (role === ROLES.EXECUTIVE_SPONSOR_LEAD) return "ESL view";
  return "Exec ops view";
};
